import React from 'react';
import Autosuggest from 'react-autosuggest';
import { compose, lifecycle, withHandlers, withStateHandlers } from 'recompose';
import { TagItemClickable } from '../../../../../common/components/TagItem/TagItemClickable';
import { ENTER_CODE } from '@mm/constants/keys-codes';
import {
  HandlerMethodTypes,
  LifeCycleThis,
  StateUpdaterMethodTypes,
} from '@mm/utils';
import SuggestionModel from '@mm/state/model/SuggestionModel';
import TriggerModel, { Phrase } from '@mm/state/model/TriggerModel';
import '../../../../../common/scss/input.scss';
import './TriggerPhrases.scss';
import arrowIcon from 'assets/icons/arrow.svg';
import { PrimaryButton } from '../../../../../common/buttons/PrimaryButton/PrimaryButton';

type OuterProps = {
  trigger: TriggerModel;
  existingPhrases: string[];
  suggestions: SuggestionModel[];
  onAdd: (trigger: TriggerModel, phrase: string) => void;
  onUpdate: (trigger: TriggerModel, phrase: Phrase) => void;
  onRemove: (index: number) => void;
};
type InitialState = ReturnType<typeof initialState>;
type StateUpdaters = StateUpdaterMethodTypes<
  InitialState,
  OuterProps,
  typeof stateUpdaters
>;

type HandlerProps = OuterProps & InitialState & StateUpdaters;
type HandlerMethods = HandlerMethodTypes<HandlerProps, typeof handlers>;

type InnerProps = InitialState & StateUpdaters & HandlerMethods;

type Props = InnerProps & OuterProps;

type AutosuggestRef = {
  input: HTMLInputElement;
};

const initialState = () => ({
  value: '',
  editedPhraseId: null as number | null | undefined,
  filteredSuggestions: [] as SuggestionModel[],
  autoSuggestRef: null as AutosuggestRef | null,
});

const stateUpdaters = {
  setValue: () => (value: string) => ({ value }),
  setEditedPhraseId: () => (editedPhraseId?: number | null) => ({
    editedPhraseId,
  }),
  resetEditor: () => () => ({ value: '', editedPhraseId: null }),
  setFilteredSuggestions: () => (filteredSuggestions: SuggestionModel[]) => ({
    filteredSuggestions,
  }),
  setAutoSuggestRef: () => (autoSuggestRef: any) => ({ autoSuggestRef }),
};

const onSuggestionsFetchRequested =
  (props: HandlerProps) =>
  ({ value }) => {
    const { suggestions, existingPhrases, setFilteredSuggestions } = props;

    const filteredSuggestions = suggestions
      .filter(s => !existingPhrases.includes(s.missedText))
      .filter(s => s.missedText.startsWith(value))
      .sort((a, b) => b.occurrences - a.occurrences);

    setFilteredSuggestions(filteredSuggestions);
  };

const onFinishedEditing =
  (props: HandlerProps) =>
  (reset: boolean = true) => {
    const {
      existingPhrases,
      editedPhraseId,
      value,
      onAdd,
      onUpdate,
      trigger,
      resetEditor,
    } = props;

    const phraseExists = existingPhrases
      .map(p => p.toLowerCase())
      .includes(value.toLowerCase());

    if (!phraseExists && value) {
      if (editedPhraseId) {
        const existingPhrase = trigger.phrases.find(
          p => p.id === editedPhraseId,
        );
        if (existingPhrase) {
          onUpdate(trigger, {
            ...existingPhrase,
            phrase: value,
          });
        }
      } else {
        onAdd(trigger, value);
      }
    }
    if (reset) {
      resetEditor();
    }
  };

const onChange =
  ({ setValue, resetEditor, autoSuggestRef }: HandlerProps) =>
  (event, { newValue, method }) => {
    switch (method) {
      case 'down':
      case 'up':
      case 'type':
        setValue(newValue);
        break;
      case 'escape':
        resetEditor();
        setTimeout(() => autoSuggestRef && autoSuggestRef.input.blur(), 10);
        break;
      default:
        break;
    }
  };

const renderSuggestion =
  () =>
  (suggestion, { isHighlighted }) =>
    (
      <div
        className={`trigger-phrases__suggestions-item
      ${isHighlighted ? 'trigger-phrases__suggestions-item--selected' : ''}
    `}
      >
        <span>{suggestion.missedText}</span>
        <span>
          <b>Hits: </b>
          {suggestion.occurrences}
        </span>
      </div>
    );

const onTagItemEdit = (props: HandlerProps) => (i: number) => {
  const {
    autoSuggestRef,
    setValue,
    setEditedPhraseId,
    trigger: { phrases },
  } = props;
  const phraseLength = phrases[i].phrase.length;

  setValue(phrases[i].phrase);
  setEditedPhraseId(phrases[i].id);

  if (autoSuggestRef) {
    setTimeout(() => {
      autoSuggestRef.input.focus();
      autoSuggestRef.input.setSelectionRange(phraseLength, phraseLength);
    }, 10);
  }
};

const handlers = {
  onSuggestionsFetchRequested,
  onFinishedEditing,
  onChange,
  renderSuggestion,
  onTagItemEdit,
};

function componentWillUnmount(this: LifeCycleThis<Props>) {
  if (this.props.value) {
    this.props.onFinishedEditing(false);
  }
}

const enhance = compose<InnerProps, OuterProps>(
  withStateHandlers(initialState, stateUpdaters),
  withHandlers(handlers),
  lifecycle<Props, {}>({ componentWillUnmount }),
);

const TriggerPhrases = (props: Props) => {
  const {
    trigger,
    value,
    setValue,
    filteredSuggestions,
    editedPhraseId,
    onChange,
    onFinishedEditing,
    onAdd,
    onRemove,
    onTagItemEdit,
    onSuggestionsFetchRequested,
    setFilteredSuggestions,
    renderSuggestion,
    setAutoSuggestRef,
  } = props;

  const { phrases } = trigger;

  const inputProps = {
    placeholder: 'e.g. "buy a home", prices, etc.',
    className: 'form-control reactstrap-input',
    value,
    onChange,
    onBlur: () => onFinishedEditing(),
    onKeyDown: e => e.keyCode === ENTER_CODE && onFinishedEditing(),
  };

  return (
    <div className="trigger-phrases">
      <div className="trigger-phrases__input-with-arrow">
        <div className="trigger-phrases__input chatbot__input-group">
          <Autosuggest<SuggestionModel>
            ref={setAutoSuggestRef}
            suggestions={filteredSuggestions}
            shouldRenderSuggestions={() => false}
            onSuggestionSelected={(e, s) => {
              onAdd(trigger, s.suggestionValue);
              setValue('');
            }}
            onSuggestionsFetchRequested={onSuggestionsFetchRequested}
            onSuggestionsClearRequested={() => setFilteredSuggestions([])}
            getSuggestionValue={s => s.missedText}
            renderSuggestion={renderSuggestion}
            inputProps={inputProps}
          />
        </div>
        <PrimaryButton
          className="trigger-phrases__primary-button"
          onClick={() => onAdd(trigger, value)}
          disabled={!value.length}
        >
          Add
        </PrimaryButton>
        <div className="trigger-phrases__icon-container">
          <img src={arrowIcon} className="trigger-phrases__arrow-icon" />
        </div>
      </div>
      <div className="trigger-phrases__phrases-cloud">
        {phrases.map((p, i) => {
          const saving = !p.id;
          const editing = !!(p.id && p.id === editedPhraseId);
          return (
            <TagItemClickable
              key={p.phrase}
              text={p.phrase}
              editing={editing}
              saving={saving}
              onDelete={() => onRemove(i)}
              onClick={() => onTagItemEdit(i)}
              className="trigger-phrases__tag-item"
            />
          );
        })}
      </div>
    </div>
  );
};
export default enhance(TriggerPhrases);
